import { validationConstants } from "constants/formValidation";
import { orderTypeConstants } from "constants/order";
import DisclaimerDialog from "pages/OrderOverview/components/Overview/components/DisclaimerDialog";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { IOverviewPage } from "types/Customize";
import { OrderType } from "types/Order/OrderType";

import { disclaimerDefaultValues } from "../../helper";
import { EditableField, PreviewIntro, StyledPreviewDataTag } from "../preview";
import { StyledPreviewShell } from "../preview/styled";
import { DisclaimerFields } from "./components/DisclaimerFields";
import { DisplaySettingsCard } from "./components/DisplaySettingsCard";
import {
  StyledAmount,
  StyledAmountColumn,
  StyledCheckoutPageCard,
  StyledDetailsPanel,
  StyledDiscountNote,
  StyledExpiryNote,
  StyledLinkButton,
  StyledPaymentToggle,
  StyledPlaceholderNote,
  StyledPrice,
  StyledPriceContainer,
  StyledPreviewColumns,
  StyledProviderLine,
  StyledSavePill,
  StyledSectionLabel,
  StyledServiceDate,
  StyledServiceMeta,
  StyledServiceName,
  StyledStaticCaption,
  StyledStrikePrice,
  StyledSummaryColumns,
  StyledSummaryContainer,
  StyledToggleOption,
} from "./styled";

/** Stand-in amounts so the preview reads like a real order. */
const previewAmounts = {
  facilityFee: "$1,250.00",
  physicianFee: "$300.00",
  clearPrice: "$1,395.00",
  fullPrice: "$1,550.00",
  percentOff: "10",
  expiresOn: "March 14, 2026",
};

/** Stand-in service so the preview reads like a real order. */
const previewService = {
  dateOfService: "03/14/2026",
  name: "Procedure",
  cpt: "45380",
  quantity: 1,
  providers: [
    "Dr. Physician",
    "General Hospital",
    "Dr. Pathology",
    "Dr. Anesthesiologist",
  ],
};

export function CheckoutPagePreview({
  customFields,
  orderType,
}: {
  customFields: IOverviewPage;
  orderType: OrderType;
}) {
  const { setValue, watch } = useFormContext();
  const isGFE = orderType === orderTypeConstants.GFE;

  const [showDetails, setShowDetails] = useState(false);
  const [disclaimerVisible, setDisclaimerVisible] = useState(false);

  useEffect(() => {
    setValue(`${orderType}-headlineCheckout`, customFields?.headline);
    setValue(`${orderType}-physicianEstimate`, customFields?.physicianEstimate);
    setValue(
      `${orderType}-estimateDescription`,
      customFields?.estimateDescription
    );
    setValue(
      `${orderType}-estimateTitle`,
      customFields?.estimateTitle || "Estimated Payment Due"
    );
    setValue(
      `${orderType}-facilityFeeDescription`,
      customFields?.facilityFeeDescription || "Facility Fee"
    );
    setValue(`${orderType}-facilityEstimate`, customFields?.facilityEstimate);
    setValue(`${orderType}-discountExpires`, customFields?.discountExpires);
    setValue(
      `${orderType}-discountPercentTotal`,
      customFields?.discountPercentTotal !== undefined
        ? customFields?.discountPercentTotal
        : true
    );
    setValue(
      `${orderType}-clearPriceDescription`,
      customFields?.clearPriceDescription
    );
    setValue(
      `${orderType}-clearPriceTitle`,
      customFields?.clearPriceTitle || "Pay Now Price"
    );

    if (isGFE) {
      setValue(
        "GFE-disclaimerTitleEnglish",
        customFields?.disclaimerTitleEnglish ||
          disclaimerDefaultValues.disclaimerTitleEnglish
      );
      setValue(
        "GFE-disclaimerDescriptionEnglish",
        customFields?.disclaimerDescriptionEnglish ||
          disclaimerDefaultValues.disclaimerDescriptionEnglish
      );
      setValue(
        "GFE-disclaimerTitleSpanish",
        customFields?.disclaimerTitleSpanish ||
          disclaimerDefaultValues.disclaimerTitleSpanish
      );
      setValue(
        "GFE-disclaimerDescriptionSpanish",
        customFields?.disclaimerDescriptionSpanish ||
          disclaimerDefaultValues.disclaimerDescriptionSpanish
      );
    }
  }, [customFields, setValue, orderType, isGFE]);

  const physicianEstimate = watch(`${orderType}-physicianEstimate`);
  const facilityEstimate = watch(`${orderType}-facilityEstimate`);
  const discountExpires = watch(`${orderType}-discountExpires`);
  const discountPercentRaw = watch(`${orderType}-discountPercentTotal`);
  // Matches the patient page, which treats an unset value as on.
  const discountPercentTotal =
    discountPercentRaw === undefined || discountPercentRaw === true;

  // The modal reads live form values, so edits below appear in it immediately.
  const disclaimer = {
    englishTitle: watch("GFE-disclaimerTitleEnglish"),
    englishDescription: watch("GFE-disclaimerDescriptionEnglish"),
    spanishTitle: watch("GFE-disclaimerTitleSpanish"),
    spanishDescription: watch("GFE-disclaimerDescriptionSpanish"),
  };

  return (
    <StyledPreviewShell data-test-id="customize-checkout-preview">
      <PreviewIntro
        badge="Live preview of the patient checkout page"
        title="Customize the Checkout page"
      >
        Use the <strong>display settings</strong> to choose which sections
        patients see, then edit any field with a <strong>dashed outline</strong>{" "}
        in the preview. Hit <strong>Save Changes</strong> to publish going
        forward.
      </PreviewIntro>

      <StyledPreviewColumns>
        <DisplaySettingsCard orderType={orderType} />

        <StyledCheckoutPageCard>
          <EditableField
            name={`${orderType}-headlineCheckout`}
            label="HEADLINE"
            variant="headline"
            rules={{ required: validationConstants.CUSTOMIZE_FORM.HEADLINE }}
          />

          <StyledSummaryContainer>
            <StyledPreviewDataTag>PREVIEW DATA</StyledPreviewDataTag>

            <StyledSectionLabel>SERVICES PROVIDED</StyledSectionLabel>
            <StyledServiceDate>
              {previewService.dateOfService}
            </StyledServiceDate>
            <StyledServiceName>{previewService.name}</StyledServiceName>
            <StyledServiceMeta>
              CPT: {previewService.cpt}, Qty:{previewService.quantity}
            </StyledServiceMeta>
            {previewService.providers.map((provider) => (
              <StyledProviderLine key={provider}>{provider}</StyledProviderLine>
            ))}
          </StyledSummaryContainer>

          <StyledSummaryContainer>
            <StyledSectionLabel>ACCOUNT SUMMARY</StyledSectionLabel>

            <StyledSummaryColumns>
              <div>
                <EditableField
                  name={`${orderType}-estimateTitle`}
                  label="ESTIMATE TITLE"
                  variant="title"
                  rules={{
                    required: validationConstants.CUSTOMIZE_FORM.ESTIMATE_TITLE,
                  }}
                />
                <EditableField
                  name={`${orderType}-estimateDescription`}
                  label="ESTIMATE DESCRIPTION"
                  variant="body"
                  rules={{
                    required:
                      validationConstants.CUSTOMIZE_FORM.ESTIMATE_DESCRIPTION,
                  }}
                />
              </div>

              <StyledAmountColumn>
                {facilityEstimate && (
                  <>
                    <EditableField
                      name={`${orderType}-facilityFeeDescription`}
                      label="FACILITY FEE LABEL"
                      variant="caption"
                      rules={{
                        required:
                          validationConstants.CUSTOMIZE_FORM
                            .FACILITY_DESCRIPTION,
                      }}
                    />
                    <StyledAmount>{previewAmounts.facilityFee}</StyledAmount>
                  </>
                )}

                {physicianEstimate && (
                  <div className="mt-3">
                    <StyledStaticCaption>Physician fee</StyledStaticCaption>
                    <StyledAmount>{previewAmounts.physicianFee}</StyledAmount>
                  </div>
                )}
              </StyledAmountColumn>
            </StyledSummaryColumns>

            {isGFE && (
              <div className="mt-3">
                <StyledLinkButton
                  type="button"
                  data-test-id="preview-why-do-i-owe"
                  onClick={() => setShowDetails((shown) => !shown)}
                >
                  {showDetails ? "Hide Details" : "Why do I owe this amount?"}
                  <i
                    className={`pi pi-chevron-up ${
                      showDetails ? "" : "rotate"
                    }`}
                  />
                </StyledLinkButton>

                {showDetails && (
                  <StyledDetailsPanel>
                    <StyledPlaceholderNote>
                      The patient sees their insurance and fee breakdown here.
                      It is built from the order, so there is nothing to
                      customize.
                    </StyledPlaceholderNote>

                    <StyledLinkButton
                      type="button"
                      data-test-id="preview-open-disclaimer"
                      onClick={() => setDisclaimerVisible(true)}
                    >
                      {disclaimer.englishTitle}
                    </StyledLinkButton>
                  </StyledDetailsPanel>
                )}

                <DisclaimerDialog
                  visible={disclaimerVisible}
                  onClose={() => setDisclaimerVisible(false)}
                  englishTitle={disclaimer.englishTitle}
                  englishDescription={disclaimer.englishDescription}
                  spanishTitle={disclaimer.spanishTitle}
                  spanishDescription={disclaimer.spanishDescription}
                />
              </div>
            )}
          </StyledSummaryContainer>

          <StyledPaymentToggle aria-hidden="true">
            <StyledToggleOption $active>
              Pay in Full
              {discountPercentTotal && (
                <StyledSavePill>
                  Save {previewAmounts.percentOff}%
                </StyledSavePill>
              )}
            </StyledToggleOption>
            <StyledToggleOption>Payment Plan</StyledToggleOption>
          </StyledPaymentToggle>

          <StyledPriceContainer>
            <StyledPreviewDataTag>PREVIEW DATA</StyledPreviewDataTag>

            <div>
              <EditableField
                name={`${orderType}-clearPriceTitle`}
                label="PAY NOW TITLE"
                variant="title"
                rules={{
                  required:
                    validationConstants.CUSTOMIZE_FORM.CLEAR_PRICE_TITLE,
                }}
              />
              <EditableField
                name={`${orderType}-clearPriceDescription`}
                label="PAY NOW DESCRIPTION"
                variant="body"
                rules={{
                  required:
                    validationConstants.CUSTOMIZE_FORM.CLEAR_PRICE_DESCRIPTION,
                }}
              />
            </div>

            <div>
              <StyledPrice>{previewAmounts.clearPrice}</StyledPrice>
              {discountPercentTotal && (
                <>
                  <StyledDiscountNote>
                    {previewAmounts.percentOff}% off
                  </StyledDiscountNote>
                  <StyledStrikePrice>
                    Total price: <span>{previewAmounts.fullPrice}</span>
                  </StyledStrikePrice>
                </>
              )}
            </div>
          </StyledPriceContainer>

          {discountExpires && (
            <StyledExpiryNote>
              Discount expires on {previewAmounts.expiresOn}
            </StyledExpiryNote>
          )}
        </StyledCheckoutPageCard>
      </StyledPreviewColumns>

      {isGFE && <DisclaimerFields />}
    </StyledPreviewShell>
  );
}
